/** The Schedule keeps the two weeks it shows in the address. */
import { z } from 'zod'

export const scheduleSearchSchema = z.object({
  /** Any day in the first week shown; this week when absent. */
  from: z.iso.date().optional().catch(undefined),
})
export type ScheduleSearch = z.infer<typeof scheduleSearchSchema>

/** The days on screen, by date: stays true when two earlier weeks are put in front. */
export interface StripView {
  first: string
  last: string
}

/**
 * Which columns draw their cards: those within `margin` of the days on screen.
 * Found by date, not by position, so the columns on screen keep their cards in
 * the very render that puts two earlier weeks in front of them.
 */
export function drawnRange(
  keys: readonly string[],
  view: StripView,
  margin: number,
): { from: number; to: number } {
  const first = keys.indexOf(view.first)
  const last = keys.indexOf(view.last)
  if (first < 0 || last < 0) return { from: 0, to: -1 }
  return { from: first - margin, to: last + margin }
}

/**
 * Where the strip scrolls so `anchor` — the first day that was on screen —
 * stays the first day on screen once earlier weeks are in front of it. A
 * position, not a nudge: the browser may already have kept the day in place
 * (Chrome re-snaps to it), and adding the new weeks' width on top of that sent
 * the view two weeks ahead.
 */
export function anchoredScroll(keys: readonly string[], anchor: string, columnWidth: number) {
  const index = keys.indexOf(anchor)
  return index < 0 ? null : index * columnWidth
}
